/**
 * Monotonic turn generation — incremented on stream start, cancel, and conversation switch.
 * Client tools capture the generation when they begin; stale completions are dropped.
 */
export type TurnSession = {
  generation: number;
  aborted: boolean;
};

export function createTurnSession(): TurnSession {
  return { generation: 0, aborted: false };
}

export function bumpTurnGeneration(session: TurnSession, abort = false): number {
  session.generation += 1;
  if (abort) {
    session.aborted = true;
  } else {
    session.aborted = false;
  }
  return session.generation;
}

export function isTurnStale(session: TurnSession, captured: number): boolean {
  return session.generation !== captured || session.aborted;
}

export type RestoreMutex = {
  abortController: AbortController | null;
  inFlight: Promise<void> | null;
  targetInteractionId: string | null;
};

export function createRestoreMutex(): RestoreMutex {
  return { abortController: null, inFlight: null, targetInteractionId: null };
}

export function abortRestore(mutex: RestoreMutex): void {
  mutex.abortController?.abort();
  mutex.abortController = null;
  mutex.inFlight = null;
  mutex.targetInteractionId = null;
}

export async function withRestoreMutex<T>(
  mutex: RestoreMutex,
  interactionId: string,
  fn: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  abortRestore(mutex);
  const controller = new AbortController();
  mutex.abortController = controller;
  mutex.targetInteractionId = interactionId;

  const run = fn(controller.signal);
  mutex.inFlight = run.then(() => undefined).catch(() => undefined);

  try {
    return await run;
  } finally {
    if (mutex.abortController === controller) {
      mutex.abortController = null;
      mutex.inFlight = null;
      mutex.targetInteractionId = null;
    }
  }
}
